import { useEffect, useState } from 'react';
import { Actions, FSpread } from '@client/new/helpers/types';
import { transformPrice, PriceRangeType } from './editBundle.vm.mapper';
import { Control, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ButtonVariant } from '@client/new/pages/workout/components/addFromLibrary/mappers';
import toast from 'react-hot-toast';
import { getStepCta, mapToCreateBundleInput } from './createBundle.mapper';
import {
  createBundlePriceFormSchema,
  CreateFormValues,
  formDefaultValues,
} from './priceFormSchemas';
import { useCreateBundleModel } from '../model/createBundle.model';
import { ApolloQueryResult } from '@apollo/client';
import { GetSubscriptionBundlesQuery } from 'generated/graphql';
import { trackEvent } from '@services/mixpanel';

type PayloadMap = {
  start: null;
  setStep: CreateModalSteps;
  create: null;
  close: null;
  cancel: null;
  transformPrice: {
    value: string;
    type: PriceRangeType;
  };
};

export type CreateBundleAction = Actions<PayloadMap>;
export type CreateBundleDispatcher = FSpread<PayloadMap>;

export type CreateBundleVM = {
  createBundleModal: {
    isOpen: boolean;
    close: () => void;
    control: Control<CreateFormValues, any, CreateFormValues>;
    error?: string;
    transformPrice: (value: string, type: PriceRangeType) => void;
    step: CreateModalSteps;
    stepCta: Array<{
      title: string;
      onClick: () => void;
      submitting?: boolean;
      variant?: ButtonVariant;
    }>;
    freeTrial: {
      disabled: {
        monthly: boolean;
        annual: boolean;
      };
    };
  };
  createBundle: () => void;
};

export type CreateModalSteps = 1 | 2 | 3;

export const useCreateBundleVM = ({
  creatorId,
  refetch,
}: {
  creatorId: number;
  refetch?:
    | (() => Promise<ApolloQueryResult<GetSubscriptionBundlesQuery>>)
    | undefined;
}): CreateBundleVM => {
  const [isSubmitting, setSubmitting] = useState(false);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [step, setStep] = useState<CreateModalSteps>(1);
  const [isFormInitialized, setIsFormInitialized] = useState(false);

  const {
    handleSubmit,
    control,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<CreateFormValues>({
    defaultValues: formDefaultValues,
    resolver: zodResolver(createBundlePriceFormSchema),
  });

  const annualPriceEnabled = watch('annualPriceEnabled');
  const monthlyPriceEnabled = watch('monthlyPriceEnabled');

  useEffect(() => {
    if (isFormInitialized) {
      trackEvent({
        type: 'Subscription price state toggled',
        payload: {
          type: annualPriceEnabled ? 'annual' : 'monthly',
          offerId: 0,
        },
      });
    }
  }, [annualPriceEnabled, monthlyPriceEnabled, isFormInitialized]);

  const { createBundle } = useCreateBundleModel();

  const onCreate = handleSubmit(
    (values) => {
      const input = mapToCreateBundleInput(values, creatorId);
      createBundle(input)
        .then((res) => {
          refetch?.();
          closeModal();
          if (input.annualPriceCents || input.monthlyPriceCents) {
            trackEvent({
              type: 'Subscription price saved',
              payload: {
                offerId: res.data?.createSubscriptionBundle.id || 0,
              },
            });
          }
        })
        .catch((e) => {
          toast.error(e.message);
        })
        .finally(() => {
          setSubmitting(false);
        });
    },
    () => {
      setSubmitting(false);
    },
  );

  const dispatch: CreateBundleDispatcher = (type, ...args) => {
    const action = { type, payload: args[0] } as CreateBundleAction;
    switch (action.type) {
      case 'create':
        setSubmitting(true);
        onCreate();
        return null;
      case 'transformPrice':
        transformPrice(action.payload.value, action.payload.type, setValue);
        return null;
      case 'close':
      case 'cancel':
        closeModal();
        return null;
      case 'start':
        trackEvent({
          type: 'Offer Exclusive Started',
          payload: {
            migrated: false,
          },
        });
        setModalOpen(true);
        setIsFormInitialized(true);
        return null;
      case 'setStep':
        setStep(action.payload);
        return null;
    }
  };

  const closeModal = () => {
    reset(formDefaultValues, {
      keepValues: false,
      keepDirty: false,
      keepDirtyValues: false,
    });
    setIsFormInitialized(false);
    setModalOpen(false);
    setStep(1);
  };

  return {
    createBundle: () => dispatch('start'),
    createBundleModal: {
      isOpen: modalOpen,
      close: () => dispatch('cancel'),
      control: control,
      error: errors.monthlyPriceEnabled?.message,
      transformPrice: (value: string, type: PriceRangeType) =>
        dispatch('transformPrice', { value, type }),
      step,
      stepCta: getStepCta({ step, dispatch, isSubmitting }),
      freeTrial: {
        disabled: {
          monthly: !monthlyPriceEnabled,
          annual: !annualPriceEnabled,
        },
      },
    },
  };
};
